import { guards } from '@shared/helpers/typeguards.helpers';
import { isCircularJson } from '../helpers/json.helpers';
import { getObjectDepth } from '../helpers/object.helpers';
import { DataTableDataItem, IDataTable } from '../models/data-table.model';
import { isValidOwnerAndOrganizations } from './organization.validation';
import { throwValidationErrors } from './validation.helpers';
import { GenericValidationError, ValidationResult } from './validation.models';
import { isDataTableDataItemValue } from '@shared/helpers/data-tables';
import { isValidVariableName } from '@shared/helpers/mathjs';

/**
 * Validates a data table object
 * @param dataTable The data table to validate
 * @returns An object containing validation status and any error messages
 */
export const isValidDataTable = (
  dataTable: IDataTable,
  validateServerDefinedProperties: boolean = true,
): ValidationResult => {
  const { id, name, data, properties, index_key } = dataTable;

  if (!guards.is.object(dataTable)) {
    return new GenericValidationError('Data table must be an object');
  }
  if (!guards.is.nonEmptyString(id)) {
    return new GenericValidationError('ID must be a string');
  }
  if (!guards.is.nonEmptyString(name)) {
    return new GenericValidationError('Name must be a string');
  }

  // Org and owner validations
  const orgErrors = validateServerDefinedProperties
    ? isValidOwnerAndOrganizations(dataTable)
    : true;
  if (orgErrors !== true) {
    return orgErrors;
  }

  for (const property of properties) {
    if (property === index_key) {
      return new GenericValidationError(
        'Selected index_key can not also be a property',
      );
    }
  }

  if (!guards.is.nonEmptyArray(properties)) {
    return new GenericValidationError('One or more properties are required');
  }

  if (properties.some((property) => property === index_key)) {
    return new GenericValidationError(
      'Selected index_key can not also be a property',
    );
  }

  // Validate each property
  for (const property of properties) {
    const propertyErrors = validateDataTableProperty(data, property);
    if (propertyErrors !== true) {
      return propertyErrors;
    }
  }

  // Check JSON for errors
  const jsonErrors = isValidDataTableData(data);
  if (jsonErrors !== true) {
    return jsonErrors;
  }

  // index_key must be provided
  if (!guards.is.nonEmptyString((dataTable as IDataTable).index_key)) {
    return new GenericValidationError('Index key is required');
  }

  // Name must be a valid variable name
  if (!isValidVariableName(name)) {
    return new GenericValidationError('Name must be a valid variable name');
  }
  return true;
};

/**
 * Validate a data table object
 * @param dataTable The data table to validate
 * @param validateServerDefinedProperties Whether to validate server defined properties ( owner, organizations)
 * @returns The validated data table
 */
export const validateDataTable = (
  dataTable: IDataTable,
  validateServerDefinedProperties?: boolean,
): IDataTable => {
  const errors = isValidDataTable(dataTable, validateServerDefinedProperties);
  if (errors !== true) {
    throwValidationErrors(errors, 'DataTableValidationError');
  }
  return dataTable;
};

const MAX_JSON_LENGTH = 10000; // How many characters are allowed in the JSON string
const MAX_JSON_DEPTH = 2; // How many levels of nested objects are allowed

export const isValidDataTableData = (
  data: IDataTable['data'],
): ValidationResult => {
  if (!guards.is.array(data) || guards.is.emptyArray(data)) {
    return new GenericValidationError(
      'JSON data must be an array of at least one object',
    );
  }

  // Check length
  const jsonString = JSON.stringify(data);
  if (jsonString.length > MAX_JSON_LENGTH) {
    return new GenericValidationError(
      `JSON data exceeds maximum length limit on ${MAX_JSON_LENGTH} characters`,
    );
  }

  // Check each item for valid values
  for (const item of data) {
    const itemErrors = validateDataTableDataItem(item);
    if (itemErrors !== true) {
      return itemErrors;
    }
  }

  // Only two levels of nesting are allowed
  if (getObjectDepth(data) > MAX_JSON_DEPTH) {
    return new GenericValidationError(
      `JSON data can have a maximum of ${MAX_JSON_DEPTH} levels of nesting`,
    );
  }

  // Check for circular references
  if (isCircularJson(data)) {
    return new GenericValidationError(
      'Circular references detected in JSON data',
    );
  }
  return true;
};

export const validateDataTableData = (json: any[]): IDataTable['data'] => {
  const errors = isValidDataTableData(json);
  if (errors !== true) {
    throwValidationErrors(errors, 'DataTableValidationError');
  }
  return json as IDataTable['data'];
};

const validateDataTableProperty = (
  data: DataTableDataItem[],
  property: string,
): ValidationResult => {
  if (!guards.is.nonEmptyString(property)) {
    return new GenericValidationError('Property has no key');
  }

  const invalidValue = data.find(
    (item) =>
      guards.is.defined(item[property]) &&
      !isDataTableDataItemValue(item[property]),
  );

  if (invalidValue) {
    return new GenericValidationError(
      `Property values must be strings or numbers (or undefined). Invalid value for property ${property}: ${invalidValue?.[property]}`,
    );
  }
  return true;
};

const validateDataTableDataItem = (
  item: DataTableDataItem,
): ValidationResult => {
  if (!guards.is.object(item)) {
    return new GenericValidationError('Data item must be an object');
  }
  for (const [key, value] of Object.entries(item)) {
    if (!guards.is.string(key)) {
      return new GenericValidationError('Data item key must be a string');
    }

    // Make sure only valid values are allowed
    if (!isDataTableDataItemValue(value)) {
      return new GenericValidationError(
        `DataItem value can't be of type: ${typeof value} for value: ${value}`,
      );
    }
  }
  return true;
};
